/**
 * A210C 自定义屏保：图片处理 + 协议帧构建（Web 端）
 *
 * 协议与 ADM 桌面端 / wilwei 调试页完全一致：
 * - 图片要求：Baseline JPEG（头部 FF D8 FF C0）320×240，≤ 65520 字节
 * - CRC16-CCITT（初值 0xFFFF，多项式 0x1021）对 JPEG 裸数据计算，作为素材唯一标识
 * - 上传：0x0220 Info（TLV）→ 0x0A20；循环 0x0221 Data（payload ≤55B）→ 0x0A21（0x10=完成）
 * - 设为屏保：0x0219 + CRC16(BE)，无应答
 */

/** 素材下载 Data 包单包 payload 上限（协议 3.3） */
export const SS_PKT_DATA_LEN = 55
/** 读取（设备→Web）Data 包单包 payload 上限（协议 4.3） */
export const SS_READ_PAYLOAD_MAX = 57
/** 图片大小上限（64KB − 16） */
export const SS_IMG_MAX_SIZE = 64 * 1024 - 16

export const SS_IMG_W = 320
export const SS_IMG_H = 240

/** CRC16-CCITT（与屏保协议文档第八章一致） */
export function crc16Ccitt(data: Uint8Array): number {
  let crc = 0xffff
  for (let i = 0; i < data.length; i++) {
    crc ^= (data[i] & 0xff) << 8
    for (let b = 0; b < 8; b++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff
    }
  }
  return crc & 0xffff
}

/**
 * 构建屏保 Info 包（0x0220，32 字节 TLV）：
 * A1(slot=0xFF) A2(size) A3(batch) A4(pkt_len) A5(total_pkts) A6(crc16)
 */
export function buildScreensaverInfoPacket(jpegSize: number, crc16: number): Uint8Array {
  const totalPkts = Math.ceil(jpegSize / SS_PKT_DATA_LEN)
  const p = new Uint8Array(32)
  p[0] = 0x02
  p[1] = 0x20
  p[2] = 0xa1
  p[3] = 0x01
  p[4] = 0xff
  p[5] = 0xa2
  p[6] = 0x05
  p[7] = 0x03
  p[8] = (jpegSize >> 24) & 0xff
  p[9] = (jpegSize >> 16) & 0xff
  p[10] = (jpegSize >> 8) & 0xff
  p[11] = jpegSize & 0xff
  p[12] = 0xa3
  p[13] = 0x02
  p[14] = 0x01
  p[15] = 0x00 // batch ack 关闭
  p[16] = 0xa4
  p[17] = 0x03
  p[18] = 0x02
  p[19] = (SS_PKT_DATA_LEN >> 8) & 0xff
  p[20] = SS_PKT_DATA_LEN & 0xff
  p[21] = 0xa5
  p[22] = 0x05
  p[23] = 0x03
  p[24] = (totalPkts >> 24) & 0xff
  p[25] = (totalPkts >> 16) & 0xff
  p[26] = (totalPkts >> 8) & 0xff
  p[27] = totalPkts & 0xff
  p[28] = 0xa6
  p[29] = 0x02
  p[30] = (crc16 >> 8) & 0xff
  p[31] = crc16 & 0xff
  return p
}

/** 构建屏保 Data 包（0x0221）：[2..5]=seq(BE32)，[6..]=payload */
export function buildScreensaverDataPacket(seq: number, payload: Uint8Array): Uint8Array {
  const p = new Uint8Array(6 + payload.length)
  p[0] = 0x02
  p[1] = 0x21
  p[2] = (seq >>> 24) & 0xff
  p[3] = (seq >> 16) & 0xff
  p[4] = (seq >> 8) & 0xff
  p[5] = seq & 0xff
  p.set(payload, 6)
  return p
}

/** 设为屏保（0x0219 + CRC16 BE），无应答包 */
export function buildSetScreensaverByCrc(crc16: number): Uint8Array {
  return new Uint8Array([0x02, 0x19, (crc16 >> 8) & 0xff, crc16 & 0xff])
}

/* ---------------- 读取当前屏保（设备→Web，与 ADM screensaver-protocol 一致） ---------------- */

/** 读取屏保握手 0x0365（64 字节），应答 0x0B65 3B：status[2] 0x00=OK 0x03=BUSY */
export function buildReadHandshake365(): Uint8Array {
  const b = new Uint8Array(64)
  b[0] = 0x03
  b[1] = 0x65
  return b
}

/** 读取屏保 Info 0x0265（64 字节），应答 0x0A65 11B */
export function buildReadInfo265(): Uint8Array {
  const b = new Uint8Array(64)
  b[0] = 0x02
  b[1] = 0x65
  return b
}

/** 读取屏保 Data 0x0265 0x01 + seq(BE32)（64 字节），应答 0x0A65 7+payload */
export function buildReadData265(seq: number): Uint8Array {
  const b = new Uint8Array(64)
  b[0] = 0x02
  b[1] = 0x65
  b[2] = 0x01
  b[3] = (seq >>> 24) & 0xff
  b[4] = (seq >>> 16) & 0xff
  b[5] = (seq >>> 8) & 0xff
  b[6] = seq & 0xff
  return b
}

/** 解析 0x0A65 Info（11B）：status / imgSize / totalPkts */
export function parseReadInfo065(
  buf: Uint8Array,
): { status: number; imgSize: number; totalPkts: number } | null {
  if (buf.length < 11 || buf[0] !== 0x0a || buf[1] !== 0x65) return null
  const rd32 = (o: number) =>
    ((buf[o] << 24) | (buf[o + 1] << 16) | (buf[o + 2] << 8) | buf[o + 3]) >>> 0
  return { status: buf[2], imgSize: rd32(3), totalPkts: rd32(7) }
}

/** 解析 0x0A65 Data（7+payload）：status / seq / payload */
export function parseReadData065(
  buf: Uint8Array,
): { status: number; seq: number; payload: Uint8Array } | null {
  if (buf.length < 7 || buf[0] !== 0x0a || buf[1] !== 0x65) return null
  const seq = ((buf[3] << 24) | (buf[4] << 16) | (buf[5] << 8) | buf[6]) >>> 0
  return { status: buf[2], seq, payload: buf.subarray(7) }
}

/** 设备读回的 Baseline JPEG 裸字节 → dataURL（可直接入 <img> / localStorage） */
export function jpegBytesToDataUrl(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return 'data:image/jpeg;base64,' + btoa(binary)
}

/**
 * 将标准 JPEG 重组为 MCU 期望的 Baseline 排列：SOI + SOF0 + DQT + DHT + SOS
 * （去掉 APP/COM 标记，确保头部为 FF D8 FF C0；段顺序与 wilwei 调试页一致）
 */
function repackAsBaseline(data: Uint8Array): Uint8Array {
  const dqt: Uint8Array[] = []
  const dht: Uint8Array[] = []
  let sof0: Uint8Array | null = null
  let sos: Uint8Array | null = null
  let i = 2
  while (i < data.length - 1) {
    if (data[i] !== 0xff) {
      i++
      continue
    }
    const marker = data[i + 1]
    if ((marker >= 0xe0 && marker <= 0xef) || marker === 0xfe) {
      const len = (data[i + 2] << 8) | data[i + 3]
      i += 2 + len
      continue
    }
    if (marker === 0xd9) break
    if (marker === 0xda) {
      sos = data.slice(i)
      break
    }
    const len = (data[i + 2] << 8) | data[i + 3]
    const seg = data.slice(i, i + 2 + len)
    if (marker === 0xdb) dqt.push(seg)
    else if (marker === 0xc0) sof0 = seg
    else if (marker === 0xc4) dht.push(seg)
    i += 2 + len
  }
  if (!sof0) throw new Error('不是 Baseline JPEG（未找到 SOF0）')
  if (!sos) throw new Error('JPEG 数据不完整（未找到 SOS）')

  const parts: Uint8Array[] = [new Uint8Array([0xff, 0xd8]), sof0, ...dqt, ...dht, sos]
  let total = 0
  for (const p of parts) total += p.length
  const out = new Uint8Array(total)
  let off = 0
  for (const p of parts) {
    out.set(p, off)
    off += p.length
  }
  return out
}

export interface ScreensaverImage {
  bytes: Uint8Array
  crc16: number
  /** 320×240 预览（dataURL，可直接入 localStorage / <img>） */
  dataUrl: string
}

/**
 * 把已绘制好的 320×240 canvas 编码为设备屏保 JPEG：
 * toBlob 质量递减直到 ≤65520B → 重组 Baseline（裁剪弹窗确认后调用）
 */
export async function encodeScreensaverCanvas(canvas: HTMLCanvasElement): Promise<ScreensaverImage> {
  let quality = 0.95
  while (quality >= 0.3) {
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    )
    if (!blob) throw new Error('JPEG 编码失败')
    const raw = new Uint8Array(await blob.arrayBuffer())
    const baseline = repackAsBaseline(raw)
    if (baseline.length <= SS_IMG_MAX_SIZE) {
      return {
        bytes: baseline,
        crc16: crc16Ccitt(baseline),
        dataUrl: canvas.toDataURL('image/jpeg', quality),
      }
    }
    quality -= 0.05
  }
  throw new Error('无法压缩到 64KB 以内')
}

/**
 * 把用户选择的图片转为设备屏保 JPEG：
 * cover 等比缩放居中裁剪到 320×240 → 编码（无裁剪交互时的直通路径）
 */
export async function createScreensaverJpeg(file: File | Blob): Promise<ScreensaverImage> {
  const bitmap = await createImageBitmap(file)
  const canvas = document.createElement('canvas')
  canvas.width = SS_IMG_W
  canvas.height = SS_IMG_H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context 不可用')
  // cover 裁剪：短边对齐，长边居中裁掉，避免拉伸变形
  const scale = Math.max(SS_IMG_W / bitmap.width, SS_IMG_H / bitmap.height)
  const dw = bitmap.width * scale
  const dh = bitmap.height * scale
  ctx.drawImage(bitmap, (SS_IMG_W - dw) / 2, (SS_IMG_H - dh) / 2, dw, dh)
  bitmap.close?.()
  return encodeScreensaverCanvas(canvas)
}
